import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type ListUserCaseAndCountOutput = {
  data: listUserCaseOutput[];
  total: number;
};

export enum CaseAcquisitionStatus {
  HISTORY = 'history',
  LEAD = 'lead',
  CONTACTED_LEAD = 'contacted_lead',
  CLIENT = 'client',
  LOST_LEAD = 'lost_lead',
  PROCESS_COMPLETED_SUCCESS = 'process_completed_success',
  PROCESS_COMPLETED_FAILURE = 'process_completed_failure',
}

export type listUserCaseOutput = {
  id: number;
  name: string;
  caseAcquisitionId: number;
  patientCity: string;
  patientState: string;
  cityStateText?: string;
  benefitType: string;
  benefitDescription?: string;
  inssStatus: string;
  isBpc: boolean;
  isCadUnico: boolean;
  hasMedicalDocument: boolean;
  observation: string;
  bpcText?: string;
  totalAcquired: number;
  createdAt: number;
};

export type MyCasesData = {
  id: number;
  name: string;
  status: CaseAcquisitionStatus;
  lastObservation?: string;
  lastObservationStatus?: CaseAcquisitionStatus;
  createdAt: number;
};

export type myAcquiredCaseInformation = {
  benefitType: string;
  benefitDescription: string;
  inssStatus: string;
  hasMedicalDocument: boolean;
  documentHasCid: boolean;
  isBpc: boolean;
  isCadUnico: boolean;
  periodRequirement: boolean;
  observation: string;
  casePatientId: {
    name: string;
    document: string;
    contacts: patientContact[];
  };
};

export type patientContact = {
  primaryContact: boolean;
  contactMethod: string;
  contact: string;
};

export type CaseAcquisitionLeadOutput = {
  id: number;
  benefitType?: string;
  benefitDescription?: string;
  inssStatus?: string;
  observation?: string;
  createdAt?: string | number;
  updatedAt?: string | number | null;

  casePatientId?: {
    id: number;
    name: string;
    document?: string;
    city?: string | null;
    state?: string | null;
    createdAt?: string | number;
    contacts?: patientContact[];
  };

  lead?: {
    id: number;
    status?: string;
    statusLabel?: string;
    createdAt?: string | number;
  };

  causeResult?: string;
};

export type CaseAcquisitionObservationOutput = {
  id?: string;
  observation?: string;
  author?: string;
  user?: { name?: string } | string;
  createdAt?: string | number;
  updatedAt?: string | number;
};

export type CaseAcquisitionTimelineOutput = {
  id?: string;
  eventType?: string;
  previousValue?: string;
  newValue?: string;
  metadata?: {
    observation?: string;
    observationId?: string;
  } | null;
  userName?: string;
  createdAt?: string | number;
};

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  private apiUrl = `${environment.api_v2}/v2/cases`;
  private casesMeApiUrl = `${environment.api_v2}/v2/cases/me`;

  constructor(private http: HttpClient) {}

  listStatesAndCity(): Observable<Record<string, string[]>> {
    return this.http.get<Record<string, string[]>>(`${this.apiUrl}/filters`);
  }

  getUserCredits(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/me/manifestations`);
  }

  acquireCaseWithCredits(caseId: number): Observable<{ message: string; status: number }> {
    return this.http.post<{ message: string; status: number }>(
      `${this.apiUrl}/acquire-case/${caseId}`,
      null
    );
  }

  changeStatusArchivedCase(
    caseId: number
  ): Observable<{ message: string; status: number; data: null }> {
    return this.http.put<{ message: string; status: number; data: null }>(
      `${this.apiUrl}/${caseId}/archive`,
      null
    );
  }

  getCaseInformationById(caseId: number): Observable<myAcquiredCaseInformation> {
    return this.http.get<myAcquiredCaseInformation>(`${this.apiUrl}/me/${caseId}/old`);
  }

  findUserCases(): Observable<{ data: MyCasesData[]; total: number }> {
    return this.http.get<{ data: MyCasesData[]; total: number }>(`${this.apiUrl}/me`);
  }

  findUserHistoryCases(pagination: {
    page: number;
    perPage: number;
  }): Observable<ListUserCaseAndCountOutput> {
    const query = new HttpParams().set('page', pagination.page).set('perPage', pagination.perPage);

    return this.http.get<ListUserCaseAndCountOutput>(`${this.apiUrl}/me/history`, {
      params: query,
    });
  }

  listAllCases(filter: {
    page: number;
    perPage: number;
    state: string;
    city: string;
  }): Observable<ListUserCaseAndCountOutput> {
    const params = Object.entries({
      page: filter.page,
      perPage: filter.perPage,
      state: filter.state,
      city: filter.city,
    }).reduce((item, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        return item.set(key, value);
      }
      return item;
    }, new HttpParams());

    return this.http.get<ListUserCaseAndCountOutput>(this.apiUrl, { params });
  }

  updateStatus(
    caseId: number,
    body: {
      status: CaseAcquisitionStatus;
      observation?: string;
    }
  ) {
    return this.http.patch(`${this.apiUrl}/me/${caseId}/status`, body);
  }

  getLeadById(leadId: number): Observable<CaseAcquisitionLeadOutput> {
    return this.http.get<CaseAcquisitionLeadOutput>(`${this.casesMeApiUrl}/${leadId}`);
  }

  listLeadObservations(leadId: number): Observable<CaseAcquisitionObservationOutput[]> {
    return this.http.get<CaseAcquisitionObservationOutput[]>(
      `${this.casesMeApiUrl}/${leadId}/observations`
    );
  }

  createLeadObservation(
    leadId: number,
    observation: string
  ): Observable<CaseAcquisitionObservationOutput> {
    return this.http.post<CaseAcquisitionObservationOutput>(
      `${this.casesMeApiUrl}/${leadId}/observations`,
      { observation }
    );
  }

  updateLeadObservation(
    leadId: number,
    observationId: string,
    observation: string
  ): Observable<CaseAcquisitionObservationOutput> {
    return this.http.patch<CaseAcquisitionObservationOutput>(
      `${this.casesMeApiUrl}/${leadId}/observations/${observationId}`,
      { observation }
    );
  }

  listLeadTimeline(leadId: number): Observable<CaseAcquisitionTimelineOutput[]> {
    return this.http.get<CaseAcquisitionTimelineOutput[]>(
      `${this.casesMeApiUrl}/${leadId}/timeline`
    );
  }
}
